import React, { useCallback, useEffect, useState, useRef } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  useNodesState,
  useEdgesState,
  useReactFlow,
  getSmoothStepPath,
  EdgeLabelRenderer,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import EditableStepNode from './EditableStepNode';
import { XMarkIcon } from '@heroicons/react/24/outline';

// Custom edge with a delete button at the midpoint
const DeletableEdge = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  label,
  labelStyle,
  labelBgStyle,
  labelBgPadding,
  labelBgBorderRadius,
  data,
}) => {
  const [hovered, setHovered] = useState(false);
  const [edgePath, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  return (
    <>
      {/* Invisible wide path for easier hovering/clicking */}
      <path
        d={edgePath}
        fill="none"
        stroke="transparent"
        strokeWidth={20}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={(e) => {
          e.stopPropagation();
          setHovered(true);
        }}
        style={{ cursor: 'pointer' }}
      />
      {/* Visible edge path */}
      <path
        d={edgePath}
        fill="none"
        stroke={hovered ? '#8b5cf6' : (style?.stroke || '#6b7280')}
        strokeWidth={hovered ? 3 : (style?.strokeWidth || 2)}
        className="react-flow__edge-path"
        style={{ transition: 'stroke 0.15s, stroke-width 0.15s' }}
      />
      <EdgeLabelRenderer>
        {/* Branch label (true/false) */}
        {label && (
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY - 14}px)`,
              pointerEvents: 'none',
              fontSize: 10,
              fontWeight: 500,
              color: '#6b7280',
              background: 'rgba(255,255,255,0.9)',
              padding: '1px 4px',
              borderRadius: 2,
            }}
          >
            {label}
          </div>
        )}
        {/* Delete button - shown on hover */}
        {hovered && (
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
              pointerEvents: 'all',
            }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (data?.onDelete) data.onDelete(id);
              }}
              className="w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-md transition-colors"
              title="Delete connection"
            >
              <XMarkIcon className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </EdgeLabelRenderer>
    </>
  );
};

const nodeTypes = { editableStepNode: EditableStepNode };
const edgeTypes = { deletable: DeletableEdge };

const CanvasInner = ({
  initialNodes,
  initialEdges,
  onNodeClick,
  onNodeDragStop,
  onConnectHandler,
  onEdgesDeleteHandler,
  onNodesDeleteHandler,
  onDropNode,
  onDeleteNode,
  selectedNodeId,
}) => {
  const { fitView, screenToFlowPosition } = useReactFlow();
  const fitViewTimeoutRef = useRef(null);

  // Use refs for handlers so useEffect doesn't re-fire when callbacks change
  const onDeleteNodeRef = useRef(onDeleteNode);
  onDeleteNodeRef.current = onDeleteNode;
  const onEdgesDeleteRef = useRef(onEdgesDeleteHandler);
  onEdgesDeleteRef.current = onEdgesDeleteHandler;

  const stableOnDeleteNode = useCallback((nodeId) => onDeleteNodeRef.current(nodeId), []);
  const stableOnEdgesDelete = useCallback((edgeId) => onEdgesDeleteRef.current(edgeId), []);

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  useEffect(() => () => {
    if (fitViewTimeoutRef.current) clearTimeout(fitViewTimeoutRef.current);
  }, []);

  // Sync when external nodes/edges change
  useEffect(() => {
    setNodes(
      initialNodes.map((n) => ({
        ...n,
        selected: n.id === selectedNodeId,
        data: { ...n.data, onDelete: stableOnDeleteNode },
      }))
    );
  }, [initialNodes, selectedNodeId, stableOnDeleteNode, setNodes]);

  useEffect(() => {
    setEdges(
      initialEdges.map((e) => ({
        ...e,
        type: 'deletable',
        data: { ...e.data, onDelete: stableOnEdgesDelete },
      }))
    );
  }, [initialEdges, stableOnEdgesDelete, setEdges]);

  const onNodeDragStopHandler = useCallback((_event, node) => {
    onNodeDragStop(node.id, node.position);
  }, [onNodeDragStop]);

  const onConnect = useCallback((params) => {
    onConnectHandler(params.source, params.target, params.sourceHandle);
  }, [onConnectHandler]);

  const onNodeClickHandler = useCallback((_event, node) => {
    onNodeClick(node.id);
  }, [onNodeClick]);

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback((event) => {
    event.preventDefault();
    const data = event.dataTransfer.getData('application/reactflow');
    if (!data) return;
    const { nodeType } = JSON.parse(data);
    const position = screenToFlowPosition({ x: event.clientX, y: event.clientY });
    onDropNode(nodeType, [Math.round(position.x), Math.round(position.y)]);
  }, [screenToFlowPosition, onDropNode]);

  const handleInit = useCallback(() => {
    if (fitViewTimeoutRef.current) clearTimeout(fitViewTimeoutRef.current);
    fitViewTimeoutRef.current = setTimeout(() => fitView({ padding: 0.3 }), 100);
  }, [fitView]);

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      onConnect={onConnect}
      onNodeDragStop={onNodeDragStopHandler}
      onNodeClick={onNodeClickHandler}
      onDrop={onDrop}
      onDragOver={onDragOver}
      nodeTypes={nodeTypes}
      edgeTypes={edgeTypes}
      defaultEdgeOptions={{
        type: 'deletable',
        style: { strokeWidth: 2, stroke: '#6b7280' },
      }}
      onInit={handleInit}
      fitView
      fitViewOptions={{ padding: 0.3 }}
      nodesDraggable={true}
      nodesConnectable={true}
      elementsSelectable={true}
      minZoom={0.2}
      maxZoom={2}
      className="bg-gray-50 dark:bg-gray-900"
    >
      <Background color="#c4b5fd" gap={20} size={1} variant="dots" />
    </ReactFlow>
  );
};

const EditorCanvas = (props) => {
  const { nodes, edges } = props;

  if (!nodes || nodes.length === 0) {
    return (
      <div style={{ width: '100%', height: '100%' }} className="bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <p className="text-sm text-gray-400">No nodes yet. Drag a node from the palette to get started.</p>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <style>{`
        .react-flow__edges {
          width: 100% !important;
          height: 100% !important;
        }
      `}</style>
      <ReactFlowProvider>
        <CanvasInner
          initialNodes={nodes}
          initialEdges={edges}
          onNodeClick={props.onNodeClick}
          onNodeDragStop={props.onNodeDragStop}
          onConnectHandler={props.onConnectHandler}
          onEdgesDeleteHandler={props.onEdgesDeleteHandler}
          onNodesDeleteHandler={props.onNodesDeleteHandler}
          onDropNode={props.onDropNode}
          onDeleteNode={props.onDeleteNode}
          selectedNodeId={props.selectedNodeId}
        />
      </ReactFlowProvider>
    </div>
  );
};

export default EditorCanvas;
